import React, { useMemo, useState, useEffect, useRef } from "react";
import "./ClientManagement.css";
import ClientFolder from "./ClientFolder";
import CreateFolderModal from "./CreateFolderModal";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  faSearch,
  faFolderPlus,
  faQuestionCircle,
  faChartBar,
  faPalette,
  faCheck,
  faUsers,
  faEllipsisV,
  faEdit,
  faTrashAlt,
} from "@fortawesome/free-solid-svg-icons";
import Modal from "./Modal";
import PageHeader from "./layout/PageHeader";
import {
  fetchClientFoldersForScope,
  fetchClientsForScope,
  createClientFolder,
  updateClientFolder,
  deleteClientFolder,
} from "../utils/api";
import { shouldUseUnityCompanyClientScope } from "../utils/unityLocationScope";
import { getClientWellSkyStatusLabel } from "../utils/clientWellSkyStatus";
import ClientAvatar from "./ClientAvatar";

// Folder colors
const folderColors = [
  "#2196F3", // Blue
  "#4CAF50", // Green
  "#FF9800", // Orange
  "#f44336", // Red
  "#9C27B0", // Purple
  "#00BCD4", // Cyan
  "#E91E63", // Pink
  "#607D8B", // Gray
];

const ClientManagement = ({
  activeLocation,
  locations = [],
  onChangeLocation,
  companyDisplayName,
  currentUser,
  onLogout,
  onLock,
  clientFolders,
  setClientFolders,
  onCreateFolder,
}) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [searchTerm, setSearchTerm] = useState("");
  const [clientMatches, setClientMatches] = useState([]);
  const [clientSearchLoading, setClientSearchLoading] = useState(false);
  const [isEditFolderModalOpen, setIsEditFolderModalOpen] = useState(false);
  const [folderToEdit, setFolderToEdit] = useState(null);
  const [editFolderName, setEditFolderName] = useState("");
  const [editFolderDescription, setEditFolderDescription] = useState("");
  const [editFolderColor, setEditFolderColor] = useState("#2196F3");
  const [isDeleteConfirmModalOpen, setIsDeleteConfirmModalOpen] =
    useState(false);
  const [folderToDelete, setFolderToDelete] = useState(null);
  const [isCreateFolderModalOpen, setIsCreateFolderModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const successToastTimerRef = useRef(null);
  const [folderMenuOpenFor, setFolderMenuOpenFor] = useState(null);
  const [createParentFolderId, setCreateParentFolderId] = useState(null);

  useEffect(() => {
    const q = (searchParams.get("q") || "").trim();
    if (q) setSearchTerm(q);
  }, [searchParams]);

  useEffect(() => {
    const term = searchTerm.trim();
    if (term.length < 2) {
      setClientMatches([]);
      setClientSearchLoading(false);
      return undefined;
    }

    let cancelled = false;
    setClientSearchLoading(true);
    const timer = setTimeout(async () => {
      try {
        const rows = await fetchClientsForScope(activeLocation, locations, {
          q: term,
        });
        if (!cancelled) {
          setClientMatches(Array.isArray(rows) ? rows.slice(0, 12) : []);
        }
      } catch (err) {
        if (!cancelled) {
          console.error("Client name search failed:", err);
          setClientMatches([]);
        }
      } finally {
        if (!cancelled) setClientSearchLoading(false);
      }
    }, 300);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [searchTerm, activeLocation, locations]);

  const getClientDisplayName = (client) => {
    const first = String(client?.firstName || "").trim();
    const last = String(client?.lastName || "").trim();
    const full = `${first} ${last}`.trim();
    return (
      full ||
      String(client?.name || client?.fullName || "Unknown Client").trim()
    );
  };

  const openClientProfile = (client) => {
    const id = client?._id || client?.id;
    if (!id) return;
    navigate(`/client-management/client/${id}/aip-analysis`);
  };

  // Fetch client folders from API whenever activeLocation changes
  useEffect(() => {
    const loadFolders = async () => {
      if (!activeLocation) return;

      const locId = activeLocation.id || activeLocation._id;
      if (!locId) {
        console.error("Active location has no ID:", activeLocation);
        return;
      }

      // Clear current folders to prevent mixing data while loading new location
      setClientFolders([]);
      setIsLoading(true);
      try {
        console.log("Fetching folders for location:", locId); // Debug log
        const folders = await fetchClientFoldersForScope(activeLocation, locations);
        setClientFolders(folders);
      } catch (error) {
        console.error("Failed to fetch client folders:", error);
      } finally {
        setIsLoading(false);
      }
    };
    loadFolders();
  }, [activeLocation, locations, setClientFolders]);

  useEffect(() => {
    const onDown = (e) => {
      if (e?.target?.closest?.(".cm-folder-menu")) return;
      setFolderMenuOpenFor(null);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, []);

  useEffect(() => {
    return () => {
      if (successToastTimerRef.current) {
        clearTimeout(successToastTimerRef.current);
      }
    };
  }, []);

  const showSuccessToast = (message) => {
    setSuccessMessage(message);
    if (successToastTimerRef.current) {
      clearTimeout(successToastTimerRef.current);
    }
    successToastTimerRef.current = setTimeout(() => {
      setSuccessMessage("");
    }, 5000);
  };

  // Override onCreateFolder to use API directly if needed, or ensure App.js passes API-enabled function
  const handleCreateFolderAPI = async (folderData) => {
    try {
      // Include locationId in the payload
      const payload = {
        ...folderData,
        locationId: activeLocation
          ? activeLocation.id || activeLocation._id
          : null,
        parentId: createParentFolderId || null,
      };

      // Call API
      const apiResult = await createClientFolder(payload);
      const newFolder = apiResult?.folder || apiResult;

      // Update local/global state only AFTER API success
      // Simplified update logic to ensure visibility
      setClientFolders((prev) => [...prev, newFolder]);
      setIsCreateFolderModalOpen(false);
      setCreateParentFolderId(null);

      // Ensure parent is expanded so the new subfolder is visible in the tree.
      if (payload.parentId) {
        setExpandedFolderIds((prev) => {
          const next = new Set(prev);
          next.add(payload.parentId);
          return next;
        });
      }

      // After creating a folder, jump into it and open "New Client" right away.
      const newId = newFolder?.id || newFolder?._id;
      if (newId) {
        navigate(`/client-management/${newId}`, {
          state: { autoOpenNewClient: true },
        });
      }
    } catch (error) {
      console.error("Failed to create folder:", error);
      alert("Failed to create folder.");
    }
  };

  const handleEditFolder = (folderId) => {
    // IMPORTANT: folderId passed from child might be 'id' or '_id'
    const folder = clientFolders.find(
      (f) => f.id === folderId || f._id === folderId
    );

    if (folder) {
      setFolderToEdit(folder);
      setEditFolderName(folder.name || folder.year); // Use name from API schema, fallback to year
      setEditFolderDescription(folder.description);
      setEditFolderColor(folder.color || "#2196F3");
      setIsEditFolderModalOpen(true);
    } else {
      console.error("Folder not found for edit:", folderId);
    }
  };

  const handleUpdateFolder = async () => {
    try {
      const updatedData = {
        name: editFolderName,
        description: editFolderDescription,
        color: editFolderColor,
      };
      // Use the correct ID for the update call
      const idToUpdate = folderToEdit.id || folderToEdit._id;

      const apiResult = await updateClientFolder(idToUpdate, updatedData);
      const updatedFolder = apiResult?.folder || apiResult;

      setClientFolders((prevFolders) =>
        prevFolders.map((folder) =>
          folder.id === idToUpdate || folder._id === idToUpdate
            ? updatedFolder
            : folder
        )
      );

      setIsEditFolderModalOpen(false);
      setFolderToEdit(null);
      setEditFolderName("");
      setEditFolderDescription("");
      showSuccessToast("Folder saved successfully");
    } catch (error) {
      console.error("Failed to update folder:", error);
      alert("Failed to update folder.");
    }
  };

  const handleCloseEditFolderModal = () => {
    setIsEditFolderModalOpen(false);
    setFolderToEdit(null);
    setEditFolderName("");
    setEditFolderDescription("");
    setEditFolderColor("#2196F3");
  };

  const handleDeleteFolder = (folderId) => {
    const folder = clientFolders.find(
      (f) => f.id === folderId || f._id === folderId
    );
    if (folder) {
      setFolderToDelete(folder);
      setIsDeleteConfirmModalOpen(true);
    } else {
      console.error("Folder not found for delete:", folderId);
    }
  };

  const confirmDeleteFolder = async () => {
    try {
      const idToDelete = folderToDelete.id || folderToDelete._id;
      await deleteClientFolder(idToDelete);
      setClientFolders((prev) =>
        prev.filter(
          (folder) => folder.id !== idToDelete && folder._id !== idToDelete
        )
      );
      setIsDeleteConfirmModalOpen(false);
      setFolderToDelete(null);
      showSuccessToast("Folder deleted successfully");
    } catch (error) {
      console.error("Failed to delete folder:", error);
      alert("Failed to delete folder.");
    }
  };

  const handleCloseDeleteConfirmModal = () => {
    setIsDeleteConfirmModalOpen(false);
    setFolderToDelete(null);
  };

  const filteredFolders = clientFolders.filter(
    (folder) =>
      !folder.parentId && // Only show root folders
      ((folder.name || folder.year || "")
        .toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
        (folder.description || "")
          .toLowerCase()
          .includes(searchTerm.toLowerCase()))
  );

  const [expandedFolderIds, setExpandedFolderIds] = useState(() => new Set());

  const foldersByParent = useMemo(() => {
    const map = new Map();
    (clientFolders || []).forEach((f) => {
      const pid = f?.parentId || null;
      if (!map.has(pid)) map.set(pid, []);
      map.get(pid).push(f);
    });
    for (const [, arr] of map.entries()) {
      arr.sort((a, b) =>
        String(a?.name || a?.year || "").localeCompare(String(b?.name || b?.year || "")),
      );
    }
    return map;
  }, [clientFolders]);
  const getFolderChildren = (parentId) => foldersByParent.get(parentId || null) || [];
  const rootFolders = useMemo(() => {
    // If user searched, keep old filter behavior (roots only).
    if (searchTerm?.trim()) return filteredFolders;
    return getFolderChildren(null);
  }, [foldersByParent, filteredFolders, searchTerm]);

  const toggleExpanded = (id) => {
    if (!id) return;
    setExpandedFolderIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // Show loading state if no active location yet
  if (!activeLocation) {
    return (
      <div
        className="client-management-container page-shell"
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "50vh",
          flexDirection: "column",
          gap: "16px",
        }}
      >
        <PageHeader
          title="Client Management"
          subtitle="Manage client profiles, medical information, and care plans in one centralized location."
          icon={<FontAwesomeIcon icon={faUsers} className="text-2xl text-white" />}
          activeLocation={activeLocation}
          locations={locations}
          onChangeLocation={onChangeLocation}
          companyDisplayName={companyDisplayName}
          currentUser={currentUser}
          onLogout={onLogout}
          onLock={onLock}
        />
        <div style={{ fontSize: "18px", color: "#666" }}>
          Loading location...
        </div>
        <div style={{ fontSize: "14px", color: "#999" }}>
          Please select a location from the dropdown above
        </div>
      </div>
    );
  }

  return (
    <div className="client-management-container page-shell">
      {successMessage && (
        <div className="cm-save-toast" role="status" aria-live="polite">
          <FontAwesomeIcon icon={faCheck} /> {successMessage}
        </div>
      )}
      <PageHeader
        title="Client Management"
        subtitle={
          shouldUseUnityCompanyClientScope(activeLocation)
            ? "Unity — Flemington & Princeton: all company clients and folders (e.g. Bill Hess) in one view."
            : "Manage client profiles, medical information, and care plans in one centralized location."
        }
        icon={<FontAwesomeIcon icon={faUsers} className="text-2xl text-white" />}
        activeLocation={activeLocation}
        locations={locations}
        onChangeLocation={onChangeLocation}
        companyDisplayName={companyDisplayName}
        currentUser={currentUser}
        onLogout={onLogout}
        onLock={onLock}
      />
      <div className={`cm-shell${folderMenuOpenFor ? " cm-shell--menu-open" : ""}`}>
        <aside className="cm-sidebar">
          <div className="cm-sidebar-header">
            <div className="cm-sidebar-title">Folders</div>
          </div>

          <div className="cm-search cm-search-folders">
            <FontAwesomeIcon icon={faSearch} className="cm-search-icon" />
            <input
              type="text"
              placeholder="Search clients or folders…"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              aria-label="Search clients or folders"
            />
          </div>

          {searchTerm.trim().length >= 2 ? (
            <div className="cm-client-search" aria-live="polite">
              <div className="cm-client-search__label">Clients</div>
              {clientSearchLoading ? (
                <div className="cm-client-search__hint">Searching…</div>
              ) : clientMatches.length === 0 ? (
                <div className="cm-client-search__hint">
                  No clients named “{searchTerm.trim()}” in this location scope.
                  Try Unity Flemington or Princeton, or check DeActive clients on
                  the Dashboard.
                </div>
              ) : (
                <ul className="cm-client-search__list">
                  {clientMatches.map((client) => {
                    const id = client._id || client.id;
                    const status = getClientWellSkyStatusLabel(client);
                    return (
                      <li key={id}>
                        <button
                          type="button"
                          className="cm-client-search__item"
                          onClick={() => openClientProfile(client)}
                        >
                          <ClientAvatar client={client} size={32} alt="" />
                          <span className="cm-client-search__name">
                            {getClientDisplayName(client)}
                          </span>
                          {status ? (
                            <span
                              className={`cm-client-search__status${
                                status === "DeActive"
                                  ? " cm-client-search__status--inactive"
                                  : ""
                              }`}
                            >
                              {status}
                            </span>
                          ) : null}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          ) : null}

          <div className="cm-folder-list">
            {isLoading ? (
              <div className="loading-state">Loading folders...</div>
            ) : rootFolders.length === 0 ? (
              <div className="cm-empty">No folders found.</div>
            ) : (
              (() => {
                const renderNode = (folder, level = 0) => {
                  const id = folder.id || folder._id;
                  const displayName = folder.name || folder.year || "Unnamed Folder";
                  const count = Number(folder.clientCount) || 0;
                  const children = getFolderChildren(id);
                  const hasChildren = children.length > 0;
                  const isExpanded = expandedFolderIds.has(id);

                  return (
                    <div key={id || `${displayName}-${level}`} className="cm-folder-node">
                      <div
                        role="button"
                        tabIndex={0}
                        className="cm-folder-item"
                        style={{ paddingLeft: 10 + level * 16 }}
                        onClick={() => {
                          if (!id) return;
                          navigate(`/client-management/${id}`);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            if (!id) return;
                            navigate(`/client-management/${id}`);
                          }
                        }}
                      >
                        <span
                          className="cm-folder-icon"
                          style={
                            folder.color && folder.color.startsWith("#")
                              ? { backgroundColor: `${folder.color}22`, color: folder.color }
                              : {}
                          }
                        >
                          <FontAwesomeIcon icon={faFolderPlus} />
                        </span>
                        <span className="cm-folder-text">
                          <span className="cm-folder-name" data-fullname={displayName}>
                            {displayName}
                          </span>
                        </span>
                        <span className="cm-folder-count">{count}</span>
                        <span className="cm-folder-menu">
                          <button
                            type="button"
                            className="cm-folder-kebab"
                            aria-label="Folder actions"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setFolderMenuOpenFor((prev) => (prev === id ? null : id));
                            }}
                          >
                            <FontAwesomeIcon icon={faEllipsisV} />
                          </button>
                          {folderMenuOpenFor === id && (
                            <div className="cm-menu cm-menu-folder">
                            <button
                              type="button"
                              className="cm-menu-item"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setFolderMenuOpenFor(null);
                                setCreateParentFolderId(id);
                                setIsCreateFolderModalOpen(true);
                              }}
                            >
                              <FontAwesomeIcon
                                icon={faFolderPlus}
                                style={{ marginRight: 10 }}
                              />
                              Create Sub Folder
                            </button>
                            <button
                              type="button"
                              className="cm-menu-item"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setFolderMenuOpenFor(null);
                                handleEditFolder(id);
                              }}
                            >
                              <FontAwesomeIcon
                                icon={faEdit}
                                style={{ marginRight: 10 }}
                              />
                              Edit
                            </button>
                              <button
                                type="button"
                                className="cm-menu-item danger"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  setFolderMenuOpenFor(null);
                                  handleDeleteFolder(id);
                                }}
                              >
                                <FontAwesomeIcon icon={faTrashAlt} style={{ marginRight: 10 }} />
                                Delete
                              </button>
                            </div>
                          )}
                        </span>
                        {hasChildren && (
                          <button
                            type="button"
                            className="cm-folder-expand"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              toggleExpanded(id);
                            }}
                          >
                            {isExpanded ? "−" : "+"}
                          </button>
                        )}
                      </div>
                      {hasChildren && isExpanded && (
                        <div className="cm-folder-children">
                          {children.map((c) => renderNode(c, level + 1))}
                        </div>
                      )}
                    </div>
                  );
                };
                return rootFolders.map((f) => renderNode(f, 0));
              })()
            )}
          </div>

          <button
            type="button"
            className="cm-new-folder"
            onClick={() => {
              setCreateParentFolderId(null);
              setIsCreateFolderModalOpen(true);
            }}
          >
            <FontAwesomeIcon icon={faFolderPlus} /> New Folder
          </button>
        </aside>

        <main className="cm-main">
          <div className="cm-table-card cm-empty-main">
            <div className="cm-empty-main-banner">
              <div className="cm-empty-main-copy">
                <div className="cm-empty-main-title">Choose a folder</div>
                <div className="cm-empty-main-text">
                  Pick a folder from the left panel to see the client list.
                </div>
              </div>
              <div className="cm-empty-main-actions">
                <button
                  type="button"
                  className="cm-btn cm-btn-ask"
                  onClick={() => {
                    const locationName = activeLocation?.name || "this location";
                    navigate("/ask-albert", {
                      state: {
                        locationId: activeLocation?.id || activeLocation?._id || null,
                        contextLabel: locationName,
                        starterQuestion: `What is the most recent shift report note for clients in ${locationName}?`,
                        autoSend: true,
                      },
                    });
                  }}
                >
                  <FontAwesomeIcon icon={faQuestionCircle} /> Ask Albert
                </button>
                <button type="button" className="cm-btn cm-btn-analytics">
                  <FontAwesomeIcon icon={faChartBar} /> Analytics
                </button>
              </div>
            </div>
          </div>
        </main>
      </div>

      {/* Create Folder Modal */}
      <CreateFolderModal
        isOpen={isCreateFolderModalOpen}
        onClose={() => {
          setIsCreateFolderModalOpen(false);
          setCreateParentFolderId(null);
        }}
        onCreate={handleCreateFolderAPI}
      />

      {/* Edit Folder Modal */}
      <Modal
        isOpen={isEditFolderModalOpen}
        onClose={handleCloseEditFolderModal}
        title="Edit Folder"
      >
        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleUpdateFolder();
          }}
        >
          <div className="form-group">
            <label>Folder Name *</label>
            <input
              type="text"
              value={editFolderName}
              onChange={(e) => setEditFolderName(e.target.value)}
              required
            />
          </div>
          <div className="form-group">
            <label>Description</label>
            <textarea
              rows="3"
              value={editFolderDescription}
              onChange={(e) => setEditFolderDescription(e.target.value)}
            ></textarea>
          </div>
          <div className="form-group">
            <label>
              <FontAwesomeIcon
                icon={faPalette}
                style={{ marginRight: "8px" }}
              />{" "}
              Folder Color
            </label>
            <div
              className="color-picker-grid"
              style={{
                display: "flex",
                gap: "10px",
                marginTop: "10px",
                flexWrap: "wrap",
              }}
            >
              {folderColors.map((color) => (
                <div
                  key={color}
                  onClick={() => setEditFolderColor(color)}
                  style={{
                    width: "36px",
                    height: "36px",
                    borderRadius: "8px",
                    backgroundColor: color,
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    border:
                      editFolderColor === color
                        ? "3px solid #333"
                        : "2px solid transparent",
                    transition: "all 0.2s ease",
                  }}
                >
                  {editFolderColor === color && (
                    <FontAwesomeIcon
                      icon={faCheck}
                      style={{ color: "white", fontSize: "14px" }}
                    />
                  )}
                </div>
              ))}
            </div>
          </div>
          <div className="modal-actions">
            <button
              type="button"
              className="button cancel-button"
              onClick={handleCloseEditFolderModal}
            >
              CANCEL
            </button>
            <button type="submit" className="button update-button">
              UPDATE FOLDER
            </button>
          </div>
        </form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={isDeleteConfirmModalOpen}
        onClose={handleCloseDeleteConfirmModal}
        title="Confirm Deletion"
      >
        <p>
          Are you sure you want to delete folder "
          {folderToDelete?.name || folderToDelete?.year}"? This action cannot be
          undone.
        </p>
        <div className="modal-actions">
          <button
            type="button"
            className="button cancel-button"
            onClick={handleCloseDeleteConfirmModal}
          >
            CANCEL
          </button>
          <button
            type="button"
            className="button delete-button"
            onClick={confirmDeleteFolder}
          >
            DELETE
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default ClientManagement;
